import { t } from 'i18next'
import {
  BarChartIcon,
  BarcodeIcon,
  CustomerIcon,
  DashboardIcon,
  PercentageIcon,
  DepotIcon,
  EmailIcon,
  EuroIcon,
  HeartIcon,
  ParcelLIcon,
  PrintIcon,
  ReturnIcon,
  ShopIcon,
  UserIcon,
  UserManagementIcon,
  WorkIcon,
} from '@dhl-ui/icons'

import type { PageMenuItem } from '@dhl-ui/page'
import { useCurrentUser } from '~/common'
import { links } from '~/AppRoutes'
import { useCanViewPpp } from '~/modules/ppp/hooks'
import { isMatch } from './is-match'

export const useMenuItems = () => {
  const { hasRole } = useCurrentUser()
  const canViewPpp = useCanViewPpp()
  const dataTestid = 'side-navigation-item-'

  return [
    {
      key: 'dashboard',
      link: links.dashboardLink(),
      label: t('admin.menuItems.dashboard'),
      icon: <DashboardIcon data-testid={`${dataTestid}dashboard`} />,
      isActive: isMatch(links.dashboardLink()),
    },
    hasRole('ADMIN_USERS')
      ? {
          key: 'adminusers',
          link: links.usersLink(),
          label: t('admin.menuItems.adminusers'),
          icon: <UserIcon data-testid={`${dataTestid}adminusers`} />,
          isActive: isMatch(links.usersLink()),
        }
      : undefined,
    hasRole('USERS')
      ? {
          key: 'users',
          link: links.userListLink(),
          label: t('admin.menuItems.users'),
          icon: <UserManagementIcon data-testid={`${dataTestid}users`} />,
          isActive: isMatch(links.userListLink()),
        }
      : undefined,
    hasRole('REPORTS')
      ? {
          key: 'reports',
          link: '/reports',
          label: t('admin.menuItems.reports'),
          icon: <BarChartIcon data-testid={`${dataTestid}reports`} />,
          items: [
            {
              key: 'finance',
              label: t('admin.reportsPage.financeData'),
              icon: <BarChartIcon data-testid={`${dataTestid}finance`} />,
              link: links.financeReportLink(),
              isActive: isMatch(links.financeReportLink()),
            },
            {
              key: 'invoices',
              label: t('admin.reportsPage.invoices'),
              icon: <EuroIcon data-testid={`${dataTestid}invoices`} />,
              link: links.invoicesReportLink(),
              isActive: isMatch(links.invoicesReportLink()),
            },
            {
              key: 'businessmetrics',
              label: t('admin.reportsPage.businessMetrics'),
              icon: <BarChartIcon data-testid={`${dataTestid}businessmetrics`} />,
              link: links.businessMetricsLink(),
              isActive: isMatch(links.businessMetricsLink()),
            },
            {
              key: 'onboardingData',
              label: t('admin.reportsPage.onboardingData'),
              icon: <BarChartIcon data-testid={`${dataTestid}onboardingData`} />,
              link: links.onboardingDataReportLink(),
              isActive: isMatch(links.onboardingDataReportLink()),
            },
            hasRole('MARKETING')
              ? {
                  key: 'marketing',
                  label: t('admin.reportsPage.marketingData'),
                  icon: <UserManagementIcon data-testid={`${dataTestid}marketing`} />,
                  link: links.marketingReportLink(),
                  isActive: isMatch(links.marketingReportLink()),
                }
              : undefined,
          ].filter(Boolean) as PageMenuItem[],
        }
      : undefined,
    hasRole('ORGANISATIONS')
      ? {
          key: 'organisations',
          link: links.organisationsLink(),
          label: t('admin.menuItems.organisations'),
          icon: <WorkIcon data-testid={`${dataTestid}organisations`} />,
          isActive: isMatch(links.organisationsLink()),
        }
      : undefined,
    hasRole('PARCEL_SHOPS')
      ? {
          key: 'servicepoints',
          link: links.servicePointsLink(),
          label: t('admin.menuItems.servicepoints'),
          icon: <ShopIcon data-testid={`${dataTestid}servicepoints`} />,
          isActive: isMatch(links.servicePointsLink()),
        }
      : undefined,
    hasRole('TERMINALS')
      ? {
          key: 'terminals',
          link: links.terminalsLink(),
          label: t('admin.menuItems.terminals'),
          icon: <DepotIcon data-testid={`${dataTestid}terminals`} />,
          isActive: isMatch(links.terminalsLink()),
        }
      : undefined,
    hasRole('CCC_RETURN_HUBS')
      ? {
          key: 'returnhubsccc',
          link: links.returnHubsLink(),
          label: t('admin.menuItems.returnhubccc'),
          icon: <ReturnIcon data-testid={`${dataTestid}returnhubccc`} />,
          isActive: isMatch(links.returnHubsLink()),
        }
      : undefined,
    hasRole('CCC')
      ? {
          key: 'integrators',
          link: links.integratorsLink(),
          label: t('admin.menuItems.integrators'),
          icon: <CustomerIcon data-testid={`${dataTestid}integrators`} />,
          isActive: isMatch(links.integratorsLink()),
        }
      : undefined,
    hasRole('REPRINT_LABELS')
      ? {
          key: 'reprint',
          link: links.reprintLink(),
          label: t('admin.menuItems.reprint'),
          icon: <PrintIcon data-testid={`${dataTestid}reprint`} />,
          isActive: isMatch(links.reprintLink()),
        }
      : undefined,
    hasRole('LABEL_REQUESTS')
      ? {
          key: 'label',
          link: links.labelLink(),
          label: t('admin.menuItems.label'),
          icon: <BarcodeIcon data-testid={`${dataTestid}label`} />,
          isActive: isMatch(links.labelLink()),
        }
      : undefined,
    canViewPpp
      ? {
          key: 'ppp',
          link: links.pppLink(),
          label: t('admin.menuItems.ppp'),
          icon: <ParcelLIcon data-testid={`${dataTestid}ppp`} />,
          isActive: isMatch(links.pppLink()),
        }
      : undefined,
    hasRole('PROMOCODES')
      ? {
          key: 'promocodes',
          link: links.promoCodesLink(),
          label: t('admin.menuItems.promocodes'),
          icon: <PercentageIcon data-testid={`${dataTestid}promocodes`} />,
          isActive: isMatch(links.promoCodesLink()),
        }
      : undefined,
    hasRole('SERVICEPOINT_VENDORS')
      ? {
          key: 'servicepointVendors',
          link: links.servicePointVendorsLink(),
          label: t('admin.menuItems.servicepointVendors'),
          icon: <ShopIcon data-testid={`${dataTestid}servicepointVendors`} />,
          isActive: isMatch(links.servicePointVendorsLink()),
        }
      : undefined,
    hasRole('CCC')
      ? {
          key: 'billingunits',
          link: links.billingUnitsLink(),
          label: t('admin.menuItems.billingunits'),
          icon: <EuroIcon data-testid={`${dataTestid}billingunits`} />,
          isActive: isMatch(links.billingUnitsLink()),
        }
      : undefined,
    hasRole('EMAIL_STATUS')
      ? {
          key: 'checkemails',
          link: links.checkEmailsLink(),
          label: t('admin.menuItems.checkemails'),
          icon: <EmailIcon data-testid={`${dataTestid}checkemails`} />,
          isActive: isMatch(links.checkEmailsLink()),
        }
      : undefined,
    hasRole('MARKETING')
      ? {
          key: 'dhlExtra',
          link: '/dhl-extra',
          label: t('admin.menuItems.dhlExtra'),
          icon: <HeartIcon data-testid={`${dataTestid}dhlExtra`} />,
          subMenuItems: [
            {
              key: 'dhlExtra-promotions',
              label: t('admin.dhlExtraPage.promotions'),
              icon: <HeartIcon data-testid={`${dataTestid}dhlExtra-promotions`} />,
              link: links.promotionsOverviewLink(),
              isActive: isMatch(links.promotionsOverviewLink()),
            },
          ],
        }
      : undefined,
  ].filter(Boolean) as PageMenuItem[]
}
